import {
	FeatureServiceBinder,
	FeatureServiceProviderDefinition,
	SharedFeatureService,
} from '@feature-hub/core';
import {
	ServiceConfigProviderV1,
	ServiceConfigurationV1,
} from '@volkswagen-onehub/service-config-provider';
import { BehaviorSubject, catchError, from, map, Observable, of } from 'rxjs';
import { CalculatorProduct } from '../../hooks/calculator/useCalculator';
import { fetchDataCalculator } from './api/calculator-api';
import { checkTenant, getDataUrl } from './dataUrls/get-data-url';
import { CarlineConfiguration, Configuration } from './models/calculator';
import {
	CarlineWithOfertas,
	filtersFaContentOfertas,
	Oferta,
} from './models/ofertas';
import { OfertasV2 } from './v2';

export interface SharedStarterFeatureService extends SharedFeatureService {
	readonly '2.0.0': FeatureServiceBinder<OfertasV2>;
}

export const ofertasServiceDefinition: FeatureServiceProviderDefinition<SharedStarterFeatureService> =
	{
		id: 'es:ofertas-feature-service',
		dependencies: {
			featureServices: {
				'service-config-provider': '1.0.0',
			},
		},

		create: ({ featureServices }) => {
			const serviceConfigProvider = featureServices[
				'service-config-provider'
			] as ServiceConfigProviderV1;

			const calculatorInfo = new BehaviorSubject<CarlineConfiguration[]>([]);

			let carlineOfertasBase: CarlineWithOfertas[] = [];
			let carlineOfertasCombustion: CarlineWithOfertas[] = [];
			let carlineOfertasHibrido: CarlineWithOfertas[] = [];
			let carlineOfertasElectrico: CarlineWithOfertas[] = [];

			function filterCarlineBase(
				carlines: CarlineWithOfertas[],
				faContentCarlines: string[],
				faContentMotorizacion: string[],
			): CarlineWithOfertas[] {
				let filteredCarlines: CarlineWithOfertas[] = [];

				if (Array.isArray(faContentCarlines) && faContentCarlines.length > 0) {
					filteredCarlines = carlines.filter((carline) =>
						faContentCarlines.some(
							(faData) =>
								faData == carline.name || parseInt(faData) == carline.code,
						),
					);
					if (filteredCarlines.length === 0) {
						filteredCarlines = [...carlines];
					}
				} else {
					filteredCarlines = [...carlines];
				}

				if (
					Array.isArray(faContentMotorizacion) &&
					faContentMotorizacion.length > 0
				) {
					const contentMotorizacion: Record<string, string[]> = {
						Combustion: ['Gasolina', 'Diésel'],
						Mild_Hybrid: ['Gasolina mild Hybrid (etiqueta ECO)'],
						Hibrido_enchufable: ['Híbrido', 'Híbrido enchufable'],
						Electrico: ['Eléctrico'],
					};

					const filteredMotorizaciones = new Set<string>();

					faContentMotorizacion.forEach((tipo) => {
						const motorizaciones = contentMotorizacion[tipo];
						if (motorizaciones) {
							motorizaciones.forEach((motorizacion) =>
								filteredMotorizaciones.add(motorizacion),
							);
						}
					});

					filteredCarlines = filteredCarlines
						.map((carline) => {
							const ofertasFiltradas = carline.ofertas.filter((oferta) =>
								filteredMotorizaciones.has(oferta.motor.combustible),
							);

							return {
								...carline,
								ofertas: ofertasFiltradas,
							};
						})
						.filter((carline) => carline.ofertas.length > 0);
				}

				filteredCarlines.forEach((carline) => {
					if (carline.ofertas.length > 1) {
						carline.ofertas.sort((a, b) => b.prioridad - a.prioridad);
					}
				});

				return filteredCarlines;
			}

			function filterByMotorizacion(
				motorizacion: string[],
			): CarlineWithOfertas[] {
				const carlineMotorizacion: CarlineWithOfertas[] = [];
				const addedCarlines = new Set();
				carlineOfertasBase.forEach((carline) => {
					const carlineCopy = { ...carline, ofertas: [] as Oferta[] };
					carline.ofertas.forEach((carlineOfertas) => {
						if (motorizacion.includes(carlineOfertas.motor.combustible)) {
							carlineCopy.ofertas.push(carlineOfertas);
							if (
								carlineCopy.ofertas.length > 0 &&
								!addedCarlines.has(carline.id)
							) {
								carlineMotorizacion.push(carlineCopy);
								addedCarlines.add(carline.id);
							}
						}
					});
				});
				return carlineMotorizacion;
			}

			const ofertasFeatureService_2_0_0 = {
				featureService: {
					getOfertas: async (
						faContentData: filtersFaContentOfertas,
						dataService?: ServiceConfigurationV1,
					): Promise<CarlineWithOfertas[]> => {
						const faContentCarlines = faContentData.carlines;
						const faContentMotorizacion = faContentData.motorizaciones;

						let dataUrl: string | null;
						if (dataService) {
							// SSR
							dataUrl =
								dataService.urlOrigin +
								dataService.urlPath +
								checkTenant(dataService);
						} else {
							dataUrl = getDataUrl();
						}
						//Uncomment next line to get data in pre
						// dataUrl = dataUrl.replace('https://', 'https://pre-')
						const carlines = await fetch(dataUrl + '/getOfertas')
							.then((response) => response.json())
							.then((offers) => offers.carlines);

						carlines.sort(
							(a: CarlineWithOfertas, b: CarlineWithOfertas) =>
								b.prioridadOffers - a.prioridadOffers,
						);

						carlines.forEach((carline: CarlineWithOfertas) => {
							if (carline.ofertas) {
								carline.ofertas.forEach((oferta: Oferta) => {
									oferta.calculatedFinance = {
										rawResponse: undefined,
										summaries: undefined,
									};
								});
							}
						});

						carlineOfertasBase = filterCarlineBase(
							carlines.filter(
								(carline: { ofertas: [] }) => carline.ofertas != null,
							),
							faContentCarlines,
							faContentMotorizacion,
						);

						carlineOfertasCombustion = filterByMotorizacion([
							'Gasolina',
							'Diésel',
							'Gasolina mild Hybrid (etiqueta ECO)',
						]);

						carlineOfertasHibrido = filterByMotorizacion([
							'Híbrido',
							'Híbrido enchufable',
						]);

						carlineOfertasElectrico = filterByMotorizacion(['Eléctrico']);

						return carlineOfertasBase;
					},
					getOfertasBaseFiltered: (): CarlineWithOfertas[] => {
						return carlineOfertasBase;
					},
					getOfertasCombustion: (): CarlineWithOfertas[] => {
						return carlineOfertasCombustion;
					},
					getOfertasHibrido: (): CarlineWithOfertas[] => {
						return carlineOfertasHibrido;
					},
					getOfertasElectrico: (): CarlineWithOfertas[] => {
						return carlineOfertasElectrico;
					},
					// getOfertas: async (
					// 	dataService?: ServiceConfigurationV1,
					// ): Promise<CarlineWithOfertas[]> => {
					// 	let dataUrl: string;
					// 	if (dataService) {
					// 		// SSR
					// 		dataUrl =
					// 			dataService.urlOrigin +
					// 			dataService.urlPath +
					// 			checkTenant(dataService);
					// 	} else {
					// 		dataUrl = getDataUrl();
					// 	}
					// 	//Uncomment next line to get data in pre
					// 	// dataUrl = dataUrl.replace('https://', 'https://pre-')
					// 	return fetch(dataUrl + '/getSharedOfertas') //Deberia ser OFFERS_QUERY PREGUNTAR
					// 		.then((response) => response.json())
					// 		.then((offers) => offers.carlines);
					// },
					// setFilteredCarlines: (carlineFiltered: CarlineWithOfertas): void => {
					// 	carlineOfertas.push(carlineFiltered);
					// },
					// getFilteredCarlines: (): CarlineWithOfertas[] => carlineOfertas,
					// clearFilteredCarlines: (): void => {
					// 	carlineOfertas = [];
					// },

					getDataCalculator: (
						marketingCode: string,
					): Observable<CarlineConfiguration | null> => {
						const currentCarlineArray = calculatorInfo.getValue() || [];

						const carlineConfiguration = currentCarlineArray.find(
							(item) => item.carlineOfferID === marketingCode,
						);

						if (carlineConfiguration) {
							return of(carlineConfiguration);
						}

						return from(
							ofertasFeatureService_2_0_0.featureService.dataCalculator(
								marketingCode,
							),
						).pipe(
							map((dataCalculator) => {
								const customDataCalculator = {
									carlineOfferID: marketingCode,
									configurations: { ...dataCalculator },
									productConfig: null,
								};
								calculatorInfo.next([
									...calculatorInfo.getValue(),
									customDataCalculator,
								]);
								return customDataCalculator;
							}),
							catchError((error) => {
								console.error('Error fetching data:', error);
								return of(null);
							}),
						);
					},

					dataCalculator: async (
						marketingCode: string,
					): Promise<Configuration> => {
						const {
							urlOrigin,
							urlPath,
							customConfig: { 'X-Api-Key': apiKey } = {},
						} = (serviceConfigProvider.configs['es-oneform-bff-cdn'] || {}) as {
							urlOrigin?: string;
							urlPath?: string;
							customConfig?: { 'X-Api-Key'?: string };
						};

						if (!urlOrigin || !urlPath || !apiKey) {
							throw new Error('Faltan configuraciones para es-oneform-bff-cdn');
						}
						const data = await fetchDataCalculator(
							urlOrigin,
							urlPath,
							marketingCode,
							apiKey,
						);

						return data;
					},

					updateDataCalculator: (
						marketingCode: string,
						productConfig: CalculatorProduct,
					): void => {
						const currentData = calculatorInfo.getValue();

						const updatedData = currentData.map(
							(item: CarlineConfiguration) => {
								if (item.carlineOfferID == marketingCode) {
									return {
										...item,
										productConfig: productConfig,
									};
								}
								return item;
							},
						);

						calculatorInfo.next(updatedData);
					},
				},
			};
			return {
				'2.0.0': () => ofertasFeatureService_2_0_0,
			};
		},
	};
